import { api, useLoad } from "../api";
import { fmt, zoneOf } from "../lib/format";
import { linkHandler, navigate } from "../lib/router";
import type { UsAnalytics, UsCoverage, UsIngestEntry, UsMarketInfo, UsScanBook } from "../types";
import { Hint } from "../components/Hint";
import { UsRulesSwitch, useUsRules } from "../components/UsRules";

const CATEGORY: Record<string, string> = {
  energy: "Энергоносители",
  metals: "Металлы",
  indices: "Индексы",
  softs: "Софты",
};

const REPORTS: UsCoverage["report"][] = ["LEGACY", "DISAGG", "TFF"];

/**
 * Рынок США: отчёты CFTC Commitments of Traders. Данные и расчёты отдельные от Мосбиржи (/api/us).
 * Без цен: позиционная часть сетапа (коммерсанты, OI, публика, экстремумы); тренд и сделки — после подключения цен.
 */
export function UsPage() {
  const scan = useLoad((s) => api.usScanner(s), []);
  const markets = useLoad((s) => api.usMarkets(s), []);
  const ingest = useLoad((s) => api.usIngest(s), []);
  const [rules, setRules] = useUsRules();
  const book = useLoad((s) => api.usScannerBook(rules, s), [rules]);
  const rows = scan.data ?? [];
  const lastDate = rows.map((m) => m.state?.date).filter(Boolean).sort().pop() ?? null;
  const lastKnown = rows.map((m) => m.state?.known).filter(Boolean).sort().pop() ?? null;
  const empty = !scan.loading && !scan.error && rows.every((m) => m.state == null);

  return (
    <main className="page us journal-page">
      <section className="hero">
        <h1>Рынок США</h1>
        <p className="lede">
          Еженедельные отчёты CFTC Commitments of Traders (Brent — отчёт биржи ICE Futures Europe), только фьючерсы: коммерсанты (хеджеры), крупные спекулянты и
          мелкие трейдеры. Позиции на вторник, публикация в пятницу в 15:30 по Нью-Йорку; загрузка — в пятницу ночью по
          Москве и повтор в субботу. Цены — Yahoo Finance, каждое утро (ближайший контракт без склейки, прототип).
        </p>
        <p className="asof">
          {lastDate ? <>Последний отчёт: {fmt.date(lastDate)} (опубликован {fmt.date(lastKnown)})</> : null}
        </p>
      </section>

      {scan.error ? <p className="form-error" role="alert">Не удалось загрузить: {scan.error}</p> : null}
      {scan.loading ? <p className="muted">Загрузка…</p> : null}
      {empty ? (
        <p className="muted">
          История ещё загружается с сервера CFTC (около минуты после первого запуска). Обновите страницу позже.
        </p>
      ) : null}

      {rows.length && !empty ? (
        <section>
          <h2>Позиции на последнем отчёте</h2>
          <p className="lede">
            COT Index — положение чистой позиции коммерсантов в диапазоне последних 26 и 156 отчётов: выше 80 — покупают
            больше обычного, ниже 20 — продают. «Позиционная часть» — коммерсанты в зоне (COT 26 или WILLCO) и, кроме
            индексов, стохастик OI в нужной стороне. «Сетап» — то же плюс тренд по SMA 39 недельных цен разрешает эту сторону
            (как на Мосбирже). Сетап и сделки по правилам книги — в таблице ниже и в разделах «Сделки США» и «Бэктест США».
          </p>
          <div className="table-scroll">
            <table className="data-table us-table">
              <thead>
                <tr>
                  <th className="text">Рынок</th>
                  <th className="text">Группа</th>
                  <th>COT 26</th>
                  <th>COT 156</th>
                  <th>WILLCO</th>
                  <th>OI стох.</th>
                  <th>Мелкие 26</th>
                  <th className="text">Экстремум</th>
                  <th>Цена</th>
                  <th className="text">Тренд</th>
                  <th className="text">Позиционная часть</th>
                  <th className="text">Сетап</th>
                  <th className="text">Последнее событие</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <ScanRow key={m.code} m={m} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {book.data?.length ? <BookTable rows={book.data} rules={rules} setRules={setRules} /> : null}
      {book.error ? <p className="muted">Сетап по книге не посчитан: {book.error}</p> : null}

      {markets.data?.length ? <Coverage markets={markets.data} /> : null}
      {ingest.data ? <IngestLog rows={ingest.data} /> : null}
    </main>
  );
}

function ScanRow({ m }: { m: UsAnalytics }) {
  const s = m.state;
  const to = `/us/${encodeURIComponent(m.code)}`;
  const ev = m.events[m.events.length - 1];
  return (
    <tr className="us-row" onClick={() => navigate(to)}>
      <td className="text">
        <a href={to} onClick={(e) => { e.stopPropagation(); linkHandler(to)(e); }}>
          <b>{m.code}</b> {m.name}
        </a>
      </td>
      <td className="text">{CATEGORY[m.category] ?? m.category}</td>
      <td className={zoneClass(s?.commCotShort ?? null)}>{fmt.idx(s?.commCotShort ?? null)}</td>
      <td className={zoneClass(s?.commCotLong ?? null)}>{fmt.idx(s?.commCotLong ?? null)}</td>
      <td className={zoneClass(s?.willco ?? null, 75, 25)}>{fmt.idx(s?.willco ?? null)}</td>
      <td className={s?.oiUsed ? oiClass(s?.oiStoch ?? null) : "muted"} title={s?.oiUsed ? undefined : "у индексов не участвует"}>
        {fmt.idx(s?.oiStoch ?? null)}
      </td>
      <td className={s ? (s.crowdDir > 0 ? "up" : s.crowdDir < 0 ? "down" : undefined) : undefined}>
        {fmt.idx(s?.crowdCotShort ?? null)}
      </td>
      <td className={`text ${s && Math.abs(s.recordYears) >= 3 ? (s.recordYears > 0 ? "up" : "down") : ""}`}>
        {!s || s.recordYears === 0 ? "—" : `${s.recordYears > 0 ? "макс." : "мин."} ${Math.abs(s.recordYears)} г.`}
      </td>
      <td>{fmt.price(s?.close ?? null)}</td>
      <td className={`text ${s ? (s.trend === 1 ? "up" : s.trend === -1 ? "down" : "") : ""}`}>
        {!s ? "—" : s.trend === 1 ? "вверх" : s.trend === -1 ? "вниз" : s.trend === 2 ? "плоский" : "—"}
      </td>
      <td className={`text ${s ? (s.positionalCore > 0 ? "up" : s.positionalCore < 0 ? "down" : "") : ""}`}>
        {!s ? "—" : s.positionalCore > 0 ? "за покупку" : s.positionalCore < 0 ? "за продажу" : "нет"}
      </td>
      <td className={`text ${s ? (s.setupCore > 0 ? "up" : s.setupCore < 0 ? "down" : "") : ""}`}>
        <b>{!s ? "—" : s.setupCore > 0 ? "покупка" : s.setupCore < 0 ? "продажа" : "нет"}</b>
      </td>
      <td className="text">{ev ? <>{fmt.date(ev.date)} · {ev.title}</> : "—"}</td>
    </tr>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  CFTC_LEGACY: "CFTC, классический",
  CFTC_DISAGG: "CFTC, расширенный",
  CFTC_TFF: "CFTC, финансовый",
  ICE_COT: "ICE, Brent",
  YAHOO_PRICES: "Цены Yahoo",
};

function IngestLog({ rows }: { rows: UsIngestEntry[] }) {
  const errors = rows.filter((r) => r.status === "ERROR");
  return (
    <section>
      <details open={errors.length > 0 || rows.length === 0}>
        <summary>
          <h2 style={{ display: "inline" }}>Последние загрузки</h2>
        </summary>
        {rows.length === 0 ? (
          <p className="muted">Загрузок ещё не было. Первая запускается сама при старте сервера, затем — по пятницам.</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="text">Начало</th>
                  <th className="text">Что</th>
                  <th className="text">Рынок</th>
                  <th className="text">Статус</th>
                  <th>Строк</th>
                  <th className="text">Комментарий</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="text">{new Date(r.startedAt).toLocaleString("ru-RU")}</td>
                    <td className="text">{SOURCE_LABEL[r.source] ?? r.source}</td>
                    <td className="text">{r.market ?? "все"}</td>
                    <td className={`text ${r.status === "ERROR" ? "down" : r.status === "OK" ? "up" : ""}`}>
                      {r.status === "OK" ? "готово" : r.status === "ERROR" ? "ошибка" : "идёт"}
                    </td>
                    <td>{fmt.int(r.rows)}</td>
                    <td className="text">{r.message ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>
    </section>
  );
}

/** Какие отчёты публикуются по рынку: классический — по всем, Disaggregated — по сырью, TFF — по финансовым. */
function applies(r: UsCoverage["report"], m: UsMarketInfo, ice: boolean) {
  if (r === "LEGACY") return true;
  if (r === "TFF") return m.category === "indices" && !ice;
  return m.category !== "indices";
}

function Coverage({ markets }: { markets: UsMarketInfo[] }) {
  const nothing = markets.every((m) => m.codes.every((c) => Object.keys(c.reports).length === 0));
  return (
    <section>
      <details open={nothing}>
        <summary>
          <h2 style={{ display: "inline" }}>Загруженные данные</h2>
        </summary>
        {nothing ? (
          <p className="form-error" role="alert">
            История ещё не загружена ни по одному рынку. Если после запуска сервера прошло больше пары минут — загрузка не
            прошла: причина в логе сервера (строки «CFTC … не загружен» или «ICE … не загружен»).
          </p>
        ) : null}
        <p className="lede">
          Источник публикует до трёх видов отчёта об одних и тех же позициях — они по-разному делят участников на группы:
        </p>
        <ul className="lede">
          <li>
            <b>Классический (Legacy)</b> — коммерсанты (хеджеры), крупные спекулянты, мелкие трейдеры. С 1986 г. По нему
            работает методика Ларри Уильямса и все индикаторы на сайте.
          </li>
          <li>
            <b>Расширенный (Disaggregated)</b> — только сырьё, с 2006 г.: производители, своп-дилеры, управляющие фонды,
            прочие. Дополнительный график на странице рынка. У Brent (ICE) есть только он — классические группы рассчитаны
            из него.
          </li>
          <li>
            <b>Финансовый (TFF)</b> — только финансовые рынки (у нас индексы), с 2006 г.: дилеры, управляющие активами,
            фонды с плечом.
          </li>
        </ul>
        <p className="lede">
          Код — номер рынка в отчёте. «Основной» — ряд для расчётов; дополнительные коды (прежний контракт, стандартный
          контракт до мини, сводная строка) хранятся, чтобы при необходимости склеить историю. В ячейке — период и число
          отчётов; «не публикуется» — этого отчёта для рынка не бывает.
        </p>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text">Рынок</th>
                <th className="text">Код</th>
                <th className="text">Что это</th>
                <th className="text">Классический</th>
                <th className="text">Расширенный</th>
                <th className="text">Финансовый</th>
                <th className="text">Цены (Yahoo)</th>
              </tr>
            </thead>
            <tbody>
              {markets.flatMap((m) =>
                m.codes.map((c, i) => {
                  const ice = c.cftcCode.startsWith("ICE:");
                  return (
                    <tr key={c.cftcCode}>
                      <td className="text">{i === 0 ? <b>{m.code}</b> : ""}</td>
                      <td className="text">{c.cftcCode}{c.main ? " · основной" : ""}</td>
                      <td className="text">{c.note ?? (c.main ? m.name : "—")}</td>
                      {REPORTS.map((r) => {
                        const cv = c.reports[r];
                        const na = !applies(r, m, ice) || (!c.main && r !== "LEGACY");
                        return (
                          <td key={r} className={`text${na ? " muted" : ""}`}>
                            {cv ? (
                              <>{fmt.date(cv.first)} – {fmt.date(cv.last)} · {fmt.int(cv.weeks)}</>
                            ) : na ? (
                              "не публикуется"
                            ) : (
                              "не загружено"
                            )}
                          </td>
                        );
                      })}
                      <td className="text">
                        {i === 0 && m.prices ? (
                          m.prices.days ? (
                            <>{m.prices.symbol}: {fmt.date(m.prices.first)} – {fmt.date(m.prices.last)} · {fmt.int(m.prices.days)} дн.</>
                          ) : (
                            <>{m.prices.symbol}: не загружено</>
                          )
                        ) : ""}
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

function zoneClass(v: number | null, hi = 80, lo = 20) {
  const z = zoneOf(v, hi, lo);
  return z === "bull" ? "up" : z === "bear" ? "down" : undefined;
}

/** Стохастик OI: низкий — за покупку (зелёный), высокий — за продажу. */
function oiClass(v: number | null) {
  const z = zoneOf(v);
  return z === "bull" ? "down" : z === "bear" ? "up" : undefined;
}

const sideText = (v: number) => (v > 0 ? "покупка" : v < 0 ? "продажа" : "—");
const sideCls = (v: number) => (v > 0 ? "up" : v < 0 ? "down" : "muted");

/**
 * Сетап по книге на последнем отчёте (ТЗ США, задача 4): что собрано и чего не хватает, балл «Сетап 4+»,
 * сезонность и признак открытой сделки по выбранным правилам.
 */
function BookTable({ rows, rules, setRules }: { rows: UsScanBook[]; rules: "B2" | "B" | "C"; setRules: (r: "B2" | "B" | "C") => void }) {
  return (
    <section>
      <h2>Сетап по книге и сделки</h2>
      <p className="lede">
        «По книге» — коммерсанты у края диапазона и 52-недельная средняя в ту же сторону (гл. 13); «+ OI» — ещё и открытый
        интерес в нужной зоне (гл. 15). «Сетап 4+» — балл Мосбиржи из 5. Сделка открывается в первый торговый день после
        публикации отчёта, на котором сетап появился.
      </p>
      <UsRulesSwitch rules={rules} onChange={setRules} />
      <div className="table-scroll">
        <table className="data-table us-table">
          <thead>
            <tr>
              <th className="text">Рынок</th>
              <th className="text">
                Отчёт <Hint>Дата отчёта CFTC (позиции на вторник) и дата публикации — раньше неё сетап использовать нельзя.</Hint>
              </th>
              <th className="text">
                Коммерсанты <Hint>COT 26 выше 80 или WILLCO 80 и выше — за покупку; ниже 20 / 20 и ниже — за продажу (гл. 4, 10).</Hint>
              </th>
              <th className="text">
                Тренд 52 <Hint>Наклон 52-недельной средней недельных закрытий за неделю (гл. 13, с. 149): вверх — разрешены покупки, вниз — продажи.</Hint>
              </th>
              <th className="text">
                OI <Hint>Стохастик открытого интереса за год: 20 и ниже — за покупку, 80 и выше — за продажу (гл. 8, 15). У индексов не учитывается.</Hint>
              </th>
              <th className="text">
                Публика <Hint>Мелкие трейдеры в крайней позиции против сделки (COT 26 мелких 20 и ниже — за покупку, 80 и выше — за продажу). Подтверждение (гл. 5).</Hint>
              </th>
              <th className="text">
                Сезонность <Hint>Средний ход цены в ближайшие 6 недель года по прошлым годам (не меньше 5 лет). Подтверждение (гл. 10, 13).</Hint>
              </th>
              <th className="text">По книге</th>
              <th className="text">Книга + OI</th>
              <th className="text">
                Сетап 4+ <Hint>Балл покупки / продажи из 5; сетап — 4 и больше.</Hint>
              </th>
              <th className="text">
                Не хватает <Hint>Чего не хватает для сетапа по книге на последнем отчёте.</Hint>
              </th>
              <th className="text">
                Сделка <Hint>Открытая позиция по выбранным правилам.</Hint>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const to = `/us/${encodeURIComponent(r.code)}`;
              return (
                <tr key={r.code} className="us-row" onClick={() => navigate(to)}>
                  <td className="text">
                    <a href={to} onClick={(e) => { e.stopPropagation(); linkHandler(to)(e); }}>
                      <b>{r.code}</b>
                    </a>
                  </td>
                  <td className="text">{fmt.date(r.report)} → {fmt.date(r.published)}</td>
                  <td className={`text ${sideCls(r.comm)}`}>{sideText(r.comm)}</td>
                  <td className={`text ${sideCls(r.trendBook)}`}>{r.trendBook > 0 ? "вверх" : r.trendBook < 0 ? "вниз" : "—"}</td>
                  <td className={`text ${r.oiUsed ? sideCls(r.oi) : "muted"}`}>{r.oiUsed ? sideText(r.oi) : "не учит."}</td>
                  <td className={`text ${sideCls(r.crowd)}`}>{sideText(r.crowd)}</td>
                  <td className={`text ${sideCls(r.season)}`}>{r.season > 0 ? "рост" : r.season < 0 ? "спад" : "—"}</td>
                  <td className={`text ${sideCls(r.setupB2)}`}><b>{r.setupB2 ? sideText(r.setupB2) : "нет"}</b></td>
                  <td className={`text ${sideCls(r.setupB)}`}>{r.setupB ? sideText(r.setupB) : "нет"}</td>
                  <td className={`text ${sideCls(r.setupC)}`}>
                    {r.scoreBuy}/{r.scoreSell}
                    {r.setupC ? ` · ${sideText(r.setupC)}` : ""}
                  </td>
                  <td className="text muted">{r.missing || "—"}</td>
                  <td className={`text ${r.openDir == null ? "muted" : sideCls(r.openDir)}`}>
                    {r.openDir == null ? "нет" : r.openDir > 0 ? "лонг" : "шорт"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
